import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, ShieldCheck, Upload, Image as ImageIcon, RotateCcw, FileText, Building2, Pencil, FilePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Pagination, { paginate } from "@/components/Pagination";
import { toast } from "sonner";

const ARTICLES = [
  { key: "article_1", title: "Article 1 — Objet du bail" },
  { key: "article_2", title: "Article 2 — Durée du bail" },
  { key: "article_3", title: "Article 3 — Loyer" },
  { key: "article_4", title: "Article 4 — Caution" },
  { key: "article_5", title: "Article 5 — Obligations du bailleur" },
  { key: "article_6", title: "Article 6 — Obligations du locataire" },
  { key: "article_7", title: "Article 7 — État des lieux" },
  { key: "article_8", title: "Article 8 — Résiliation" },
  { key: "article_9", title: "Article 9 — Litiges" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Configuration() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [cfg, setCfg] = useState({
    devise: "FCFA", nom_societe: "rimmoGET", adresse_societe: "", telephone: "",
    tax_habitation_pct: 0,
    logo_path: "", lease_clauses: {}, lease_clauses_defaults: {},
  });
  const [logoBlobUrl, setLogoBlobUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [openUser, setOpenUser] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "agent" });
  const [userPage, setUserPage] = useState(1);
  const [tplPage, setTplPage] = useState(1);

  // Contract templates
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null);
  const [tplForm, setTplForm] = useState({ name: "", lease_clauses: {} });
  const [tplConfirmId, setTplConfirmId] = useState(null);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/contract-templates");
      setTemplates(data || []);
    } catch { /* ignore */ }
  };

  const loadLogo = async () => {
    try {
      const res = await api.get("/configuration/logo", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setLogoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch {
      setLogoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    }
  };

  const load = async () => {
    const tasks = [api.get("/configuration")];
    if (isAdmin) tasks.push(api.get("/users"));
    const [c, u] = await Promise.all(tasks);
    const data = c.data;
    setCfg({
      devise: data.devise || "FCFA",
      nom_societe: data.nom_societe || "rimmoGET",
      adresse_societe: data.adresse_societe || "",
      telephone: data.telephone || "",
      tax_habitation_pct: Number(data.tax_habitation_pct || 0),
      logo_path: data.logo_path || "",
      lease_clauses: data.lease_clauses || {},
      lease_clauses_defaults: data.lease_clauses_defaults || {},
    });
    if (data.logo_path) loadLogo();
    else setLogoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    if (u) setUsers(u.data);
  };
  useEffect(() => { load(); loadTemplates(); /* eslint-disable-next-line */ }, [isAdmin]);
  useEffect(() => () => { if (logoBlobUrl) URL.revokeObjectURL(logoBlobUrl); }, []); // eslint-disable-line

  const saveSociete = async () => {
    try {
      await api.put("/configuration", {
        devise: cfg.devise, nom_societe: cfg.nom_societe,
        adresse_societe: cfg.adresse_societe, telephone: cfg.telephone,
        tax_habitation_pct: Number(cfg.tax_habitation_pct || 0),
      });
      toast.success("Informations société enregistrées");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting same file fires change
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image trop volumineuse (max 2 Mo)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setLogoUploading(true);
    try {
      const { data } = await api.post("/configuration/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg((c) => ({ ...c, logo_path: data.path }));
      await loadLogo();
      toast.success("Logo mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'envoi");
    } finally { setLogoUploading(false); }
  };

  const onDeleteLogo = async () => {
    try {
      await api.delete("/configuration/logo");
      setCfg((c) => ({ ...c, logo_path: "" }));
      setLogoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
      toast.success("Logo supprimé");
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  const saveClauses = async () => {
    try {
      await api.put("/configuration", { lease_clauses: cfg.lease_clauses });
      toast.success("Clauses du contrat enregistrées");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  const resetClause = (key) => {
    const def = cfg.lease_clauses_defaults?.[key] || "";
    setCfg((c) => ({ ...c, lease_clauses: { ...c.lease_clauses, [key]: def } }));
  };

  const resetAllClauses = () => {
    setCfg((c) => ({ ...c, lease_clauses: { ...c.lease_clauses_defaults } }));
    toast.message("Clauses réinitialisées — n'oubliez pas d'enregistrer.");
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", newUser);
      toast.success("Utilisateur créé");
      setOpenUser(false);
      setNewUser({ email: "", password: "", name: "", role: "agent" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  const deleteUser = async () => {
    try { await api.delete(`/users/${confirmId}`); toast.success("Utilisateur supprimé"); setConfirmId(null); load(); }
    catch { toast.error("Suppression échouée"); }
  };

  // ---- Contract templates handlers ----
  const openNewTpl = () => {
    setTplEditing(null);
    setTplForm({ name: "", lease_clauses: { ...cfg.lease_clauses_defaults } });
    setTplOpen(true);
  };
  const openEditTpl = (t) => {
    setTplEditing(t.id);
    setTplForm({
      name: t.name,
      lease_clauses: { ...cfg.lease_clauses_defaults, ...(t.lease_clauses || {}) },
    });
    setTplOpen(true);
  };
  const saveTpl = async (e) => {
    e.preventDefault();
    if (!tplForm.name.trim()) { toast.error("Le nom est requis"); return; }
    try {
      if (tplEditing) await api.put(`/contract-templates/${tplEditing}`, tplForm);
      else await api.post("/contract-templates", tplForm);
      toast.success(tplEditing ? "Modèle modifié" : "Modèle créé");
      setTplOpen(false);
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };
  const deleteTpl = async () => {
    try { await api.delete(`/contract-templates/${tplConfirmId}`); toast.success("Modèle supprimé"); setTplConfirmId(null); loadTemplates(); }
    catch { toast.error("Suppression échouée"); }
  };

  return (
    <div>
      <PageHeader
        title="Configuration"
        subtitle="Paramètres société, identité visuelle, clauses du contrat de bail et gestion des utilisateurs."
        testid="page-configuration"
        accent="indigo"
      />

      <Tabs defaultValue="societe" className="w-full" data-testid="config-tabs">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-lg mb-6 inline-flex">
          <TabsTrigger value="societe" data-testid="tab-societe" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-4">
            <Building2 className="w-4 h-4 mr-2" /> Société
          </TabsTrigger>
          <TabsTrigger value="clauses" data-testid="tab-clauses" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-4">
            <FileText className="w-4 h-4 mr-2" /> Contrat de bail
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-4">
            <ShieldCheck className="w-4 h-4 mr-2" /> Utilisateurs
          </TabsTrigger>
        </TabsList>

        {/* ─── Société ─── */}
        <TabsContent value="societe" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Société</div>
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-5">Informations générales</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de la société</Label>
                  <Input value={cfg.nom_societe} onChange={(e) => setCfg({ ...cfg, nom_societe: e.target.value })} disabled={!isAdmin} data-testid="input-cfg-nom" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Devise</Label>
                    <Input value={cfg.devise} onChange={(e) => setCfg({ ...cfg, devise: e.target.value })} disabled={!isAdmin} />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input value={cfg.telephone} onChange={(e) => setCfg({ ...cfg, telephone: e.target.value })} disabled={!isAdmin} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input value={cfg.adresse_societe} onChange={(e) => setCfg({ ...cfg, adresse_societe: e.target.value })} disabled={!isAdmin} />
                </div>
                <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <Label className="text-amber-900 font-semibold">Taxe d'habitation (% déduit du loyer)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={cfg.tax_habitation_pct}
                      onChange={(e) => setCfg({ ...cfg, tax_habitation_pct: e.target.value })}
                      disabled={!isAdmin}
                      className="max-w-[140px]"
                      data-testid="input-cfg-tax-habitation"
                    />
                    <span className="text-amber-900 font-medium">%</span>
                  </div>
                  <p className="text-xs text-amber-800/80">
                    Pourcentage déduit automatiquement du montant sur chaque reçu de paiement. Laissez à <strong>0</strong> pour désactiver.
                  </p>
                </div>
                {isAdmin && (
                  <Button onClick={saveSociete} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-cfg">Enregistrer</Button>
                )}
              </div>
            </Card>

            <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Identité visuelle</div>
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-5">Logo</h2>

              <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-5 flex items-center justify-center min-h-[140px]" data-testid="logo-preview">
                {logoBlobUrl ? (
                  <img src={logoBlobUrl} alt="Logo société" className="max-h-24 max-w-full object-contain" />
                ) : (
                  <div className="text-slate-400 text-sm flex flex-col items-center">
                    <ImageIcon className="w-8 h-8 mb-2" />
                    <span>Aucun logo</span>
                  </div>
                )}
              </div>

              {isAdmin && (
                <>
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onUploadLogo} className="hidden" data-testid="input-logo-file" />
                  <div className="mt-4 flex flex-col gap-2">
                    <Button onClick={() => fileInputRef.current?.click()} disabled={logoUploading} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="btn-upload-logo">
                      <Upload className="w-4 h-4 mr-2" /> {logoUploading ? "Envoi…" : (cfg.logo_path ? "Remplacer le logo" : "Téléverser un logo")}
                    </Button>
                    {cfg.logo_path && (
                      <Button onClick={onDeleteLogo} variant="outline" className="w-full" data-testid="btn-delete-logo">
                        <Trash2 className="w-4 h-4 mr-2 text-rose-600" /> Supprimer
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">PNG, JPEG ou WEBP, max 2 Mo. Affiché en en-tête des reçus A4 et des contrats de bail.</p>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* ─── Clauses ─── */}
        <TabsContent value="clauses" className="space-y-6">
          {/* Templates list */}
          <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="card-templates">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Modèles enregistrés</div>
                <h2 className="font-heading text-xl font-bold text-slate-900">Bibliothèque de contrats</h2>
                <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                  Créez plusieurs modèles (résidentiel, magasin, courte durée…) et attribuez-les à une maison depuis sa fiche. Sans modèle attribué, c'est le « Modèle par défaut » ci-dessous qui est utilisé.
                </p>
              </div>
              {isAdmin && (
                <Button onClick={openNewTpl} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20 shrink-0" data-testid="btn-add-template">
                  <FilePlus className="w-4 h-4 mr-2" /> Nouveau modèle
                </Button>
              )}
            </div>
            {templates.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                Aucun modèle enregistré. Cliquez sur « Nouveau modèle » pour en créer.
              </div>
            ) : (
              <div className="rounded-xl overflow-hidden border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom du modèle</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Articles personnalisés</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginate(templates, tplPage).rows.map((t) => {
                      const customCount = Object.values(t.lease_clauses || {}).filter((v) => v && String(v).trim()).length;
                      return (
                        <TableRow key={t.id} className="hover:bg-slate-50/60" data-testid={`row-template-${t.id}`}>
                          <TableCell className="font-medium text-slate-900">{t.name}</TableCell>
                          <TableCell>
                            <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50 font-normal">
                              {customCount}/9 articles
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {isAdmin && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEditTpl(t)} data-testid={`btn-edit-template-${t.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setTplConfirmId(t.id)} data-testid={`btn-delete-template-${t.id}`}>
                                  <Trash2 className="w-4 h-4 text-rose-600" />
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Pagination {...paginate(templates, tplPage)} onChange={setTplPage} testid="pagination-templates" />
              </div>
            )}
          </Card>

          {/* Global default clauses editor */}
          <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Modèle par défaut</div>
                <h2 className="font-heading text-xl font-bold text-slate-900">Clauses utilisées si la maison n'a pas de modèle attribué</h2>
                <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                  Personnalisez le texte de chaque article. Les données dynamiques (parties, bien, dates, montants) sont automatiquement injectées par le système.
                </p>
              </div>
              {isAdmin && (
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" onClick={resetAllClauses} data-testid="btn-reset-all-clauses">
                    <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser tout
                  </Button>
                  <Button onClick={saveClauses} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-clauses">
                    Enregistrer
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-5">
              {ARTICLES.map((a) => {
                const value = cfg.lease_clauses?.[a.key] ?? "";
                const def = cfg.lease_clauses_defaults?.[a.key] ?? "";
                const changed = value && def && value !== def;
                return (
                  <div key={a.key} className="rounded-lg border border-slate-200 p-4 bg-slate-50/40">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-800 font-medium">{a.title}</Label>
                      <div className="flex items-center gap-2">
                        {changed && <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">Modifié</Badge>}
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => resetClause(a.key)} data-testid={`btn-reset-${a.key}`}>
                            <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser
                          </Button>
                        )}
                      </div>
                    </div>
                    <Textarea
                      value={value}
                      onChange={(e) => setCfg((c) => ({ ...c, lease_clauses: { ...c.lease_clauses, [a.key]: e.target.value } }))}
                      rows={4}
                      disabled={!isAdmin}
                      className="bg-white font-mono text-[13px] leading-relaxed"
                      data-testid={`textarea-${a.key}`}
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* ─── Utilisateurs ─── */}
        <TabsContent value="users">
          <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Sécurité</div>
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" /> Utilisateurs
                </h2>
              </div>
              {isAdmin && (
                <Button onClick={() => setOpenUser(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-add-user">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter
                </Button>
              )}
            </div>

            {!isAdmin ? (
              <p className="text-sm text-slate-500">Seul un administrateur peut gérer les utilisateurs.</p>
            ) : (
              <div className="rounded-xl overflow-hidden border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Rôle</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginate(users, userPage).rows.map((u) => (
                      <TableRow key={u.id} className="hover:bg-slate-50/60">
                        <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                        <TableCell className="text-slate-600">{u.email}</TableCell>
                        <TableCell>
                          <Badge className={u.role === "admin"
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                            : "bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100"}>{u.role}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {u.id !== user.id && (
                            <Button size="sm" variant="ghost" onClick={() => setConfirmId(u.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination {...paginate(users, userPage)} onChange={setUserPage} testid="pagination-users" />
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-3xl" data-testid="dialog-template">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {tplEditing ? "Modifier le modèle" : "Nouveau modèle de contrat"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveTpl} className="space-y-5">
            <div className="space-y-2">
              <Label>Nom du modèle *</Label>
              <Input
                value={tplForm.name}
                onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                placeholder="Ex. Résidentiel standard, Magasin commercial, Courte durée…"
                required
                data-testid="input-template-name"
              />
            </div>
            <div className="space-y-4">
              {ARTICLES.map((a) => (
                <div key={a.key} className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
                  <Label className="text-slate-800 font-medium mb-2 block">{a.title}</Label>
                  <Textarea
                    value={tplForm.lease_clauses?.[a.key] ?? ""}
                    onChange={(e) => setTplForm({
                      ...tplForm,
                      lease_clauses: { ...tplForm.lease_clauses, [a.key]: e.target.value },
                    })}
                    rows={3}
                    className="bg-white font-mono text-[13px] leading-relaxed"
                    data-testid={`tpl-textarea-${a.key}`}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTplOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-save-template">
                {tplEditing ? "Enregistrer" : "Créer le modèle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template delete confirmation */}
      <AlertDialog open={!!tplConfirmId} onOpenChange={(o) => !o && setTplConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce modèle ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les maisons qui utilisent ce modèle reviendront automatiquement au modèle par défaut.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTpl} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={openUser} onOpenChange={setOpenUser}>
        <DialogContent data-testid="dialog-user">
          <DialogHeader><DialogTitle className="font-heading">Nouvel utilisateur</DialogTitle></DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="input-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="input-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe *</Label>
              <Input type="password" required minLength={6} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="input-user-password" />
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenUser(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-user">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
